/**
 * One notice strip, in four severities.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HOUSE STYLE, LIFTED FROM THE JOURNAL'S TRADES PAGE
 *
 * Same geometry and the same four tints used there: a hairline border at 20%
 * opacity over an 8% wash, 11px text with relaxed leading, and the message in
 * the tint's own 700/400 pair so it stays legible in both themes. Nothing
 * shouts; a notice that looks like an error every time it appears is a notice
 * people stop reading.
 *
 * THE TINT CARRIES MEANING, AND ONLY ONE OF THEM IS A PROBLEM.
 *
 *   info     blue     something is in progress and will resolve itself
 *   warn     amber    expected, and nothing is broken — a closed market
 *   error    rose     the thing that should be working is not
 *   ok       emerald  resolved, for the moment after a recovery
 *
 * Calling a closed market an error is the mistake this exists to prevent:
 * the prices on screen are the close, which is correct, and painting that
 * red teaches a trader to ignore the colour that means their feed has died.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, WifiOff } from 'lucide-react'
import { cn } from '@/lib/utils'

export type NoticeTone = 'info' | 'warn' | 'error' | 'ok'

const TONE: Record<NoticeTone, { box: string; strong: string; icon: typeof Info }> = {
  info: {
    box: 'border-blue-500/20 bg-blue-500/[0.08] text-blue-700 dark:text-blue-400',
    strong: 'text-blue-800 dark:text-blue-300',
    icon: Info,
  },
  warn: {
    box: 'border-amber-500/20 bg-amber-500/[0.08] text-amber-700 dark:text-amber-400',
    strong: 'text-amber-800 dark:text-amber-300',
    icon: AlertTriangle,
  },
  error: {
    box: 'border-rose-500/20 bg-rose-500/[0.08] text-rose-700 dark:text-rose-400',
    strong: 'text-rose-800 dark:text-rose-300',
    icon: WifiOff,
  },
  ok: {
    box: 'border-emerald-500/20 bg-emerald-500/[0.08] text-emerald-700 dark:text-emerald-400',
    strong: 'text-emerald-800 dark:text-emerald-300',
    icon: CheckCircle2,
  },
}

export interface NoticeProps {
  tone: NoticeTone
  /** The short bold lead — "Market closed". Omit for a plain sentence. */
  title?: string
  children: ReactNode
  /** One action, on the right. A link or a button the caller supplies. */
  action?: ReactNode
  /** False hides the icon, for a notice inside an already-busy row. */
  icon?: boolean
  className?: string
  testId?: string
}

export function Notice({
  tone,
  title,
  children,
  action,
  icon = true,
  className,
  testId,
}: NoticeProps) {
  const t = TONE[tone]
  const Icon = t.icon

  return (
    <div
      role="status"
      data-testid={testId}
      data-tone={tone}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] leading-relaxed',
        t.box,
        className,
      )}
    >
      {icon && <Icon size={13} className="mt-[1px] shrink-0 opacity-80" />}
      <div className="min-w-0 flex-1">
        {title && <span className={cn('font-semibold', t.strong)}>{title}</span>}
        {title && ' '}
        {children}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  )
}

/** The one button shape a notice is allowed, so they do not drift apart. */
export function NoticeAction({
  onClick,
  children,
  testId,
}: {
  onClick: () => void
  children: ReactNode
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="rounded px-1.5 py-0.5 text-[11px] font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
    >
      {children}
    </button>
  )
}
